// Chart.js theming from the --site-* tokens. Ported from NardiByNature's
// LeadGen charts/theme.ts: canvas can't consume `var()` strings, so tokens
// resolve to concrete values at chart-build time.
import type { ChartOptions, TooltipOptions } from 'chart.js'

type ChartScales = NonNullable<ChartOptions<'line' | 'bar'>['scales']>

export function cssToken(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/** `#rgb`/`#rrggbb` → `rgba()`; non-hex colors pass through unchanged. */
export function withAlpha(color: string, alpha: number): string {
    const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1]
    if (!hex) return color
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16))
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export interface ChartTheme {
    accent: string
    accentSoft: string
    danger: string
    warning: string
    grid: string
    axis: string
    text: string
    textStrong: string
    surface: string
    fontMono: string
    fontBody: string
}

export function chartTheme(): ChartTheme {
    return {
        accent: cssToken('--site-accent'),
        accentSoft: cssToken('--site-accent-soft'),
        danger: cssToken('--site-danger'),
        warning: cssToken('--site-warning'),
        grid: cssToken('--site-border'),
        axis: cssToken('--site-border'),
        text: cssToken('--site-text-muted'),
        textStrong: cssToken('--site-text'),
        surface: cssToken('--site-surface-raised'),
        fontMono: cssToken('--site-font-mono'),
        fontBody: cssToken('--site-font-body'),
    }
}

export function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Recessive axes: no vertical grid, dashed horizontal grid. */
export function baseScales(t: ChartTheme, maxXTicks: number): ChartScales {
    const tickFont = { family: t.fontMono, size: 10 }
    return {
        x: {
            type: 'category',
            grid: { display: false },
            border: { color: t.axis },
            ticks: {
                color: t.text,
                font: tickFont,
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: maxXTicks,
            },
        },
        y: {
            type: 'linear',
            beginAtZero: true,
            border: { display: false, dash: [3, 4] },
            grid: { color: t.grid },
            ticks: {
                color: t.text,
                font: tickFont,
                maxTicksLimit: 5,
                precision: 0,
            },
        },
    }
}

/** Tooltip as a small raised panel. */
export function chartTooltip(t: ChartTheme): Partial<TooltipOptions> {
    return {
        backgroundColor: withAlpha(t.surface, 0.97),
        borderColor: t.axis,
        borderWidth: 1,
        titleColor: t.accent,
        titleFont: { family: t.fontMono, size: 11, weight: 'bold' },
        bodyColor: t.textStrong,
        bodyFont: { family: t.fontMono, size: 11 },
        padding: 10,
        cornerRadius: 4,
        caretSize: 5,
        displayColors: false,
    } as Partial<TooltipOptions>
}
